/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ZonalMindMap.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 23-07-2024
 * @description : Component to show the mind map for zonal.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ReactFlow,
  Controls,
  Background,
  applyEdgeChanges,
  applyNodeChanges,
  addEdge,
  Handle,
  Position,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { HandleApiActions, labCar } from "../../redux/actions";
import customConsole from "../../config/customConsole";

const CLAMPED_NODE_LABEL_STYLE = {
  fontSize: "11px",
  fontWeight: "700",
  textAlign: "center",
  lineHeight: "12px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  wordBreak: "break-word",
  overflowWrap: "anywhere",
};

// SWM Hover Tooltip Component
const SWMHoverTooltip = ({ open, swmDetails, position, isLinkedSWM }) => {
  // All changes done by Bharathan
  if (!open || !swmDetails) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        top: position?.y || 0,
        left: position?.x || 0,
        maxWidth: "400px",
        minWidth: "300px",
        zIndex: 9999,
        pointerEvents: "none", // Prevent interfering with mouse events
        border: "1px solid #ccc",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: isLinkedSWM ? "#FFD700" : "#2897DC",
          color: isLinkedSWM ? "black" : "white",
          padding: "12px 16px",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        Software Module Details
      </Box>

      {/* Content */}
      <Box
        sx={{ textAlign: "left", padding: "16px", backgroundColor: "white" }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "8px",
          }}
        >
          <strong>Name:</strong> {swmDetails.name}
        </Typography>

        {swmDetails?.version && (
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}
            >
              <strong>Version:</strong> {swmDetails.version}
            </Typography>
          </Box>
        )}

        {swmDetails.description && (
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}
            >
              <strong>Description:</strong> {swmDetails.description}
            </Typography>
          </Box>
        )}

        {/* Show network interface details for linked SWMs */}
        {isLinkedSWM && swmDetails.network_interface && (
          <>
            <Divider sx={{ marginY: "8px" }} />
            <Typography
              variant="h6"
              sx={{
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "8px",
                // color: "#FFD700",
              }}
            >
              Network Interface: {swmDetails.network_interface.interface_name}
            </Typography>
          </>
        )}
      </Box>
    </Paper>
  );
};

// EE Hover Tooltip Component
const EEHoverTooltip = ({ open, eeDetails, position }) => {
  if (!open || !eeDetails) return null;

  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        top: position?.y || 0,
        left: position?.x || 0,
        maxWidth: "400px",
        minWidth: "300px",
        zIndex: 9999,
        pointerEvents: "none", // Prevent interfering with mouse events
        border: "1px solid #ccc",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "#333",
          color: "white",
          padding: "12px 16px",
          fontSize: "14px",
          fontWeight: "bold",
        }}
      >
        EE Component Details
      </Box>

      {/* Content */}
      <Box
        sx={{ textAlign: "left", padding: "16px", backgroundColor: "white" }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "14px",
            fontWeight: "bold",
            marginBottom: "8px",
          }}
        >
          <strong>Description:</strong> {eeDetails.description}
        </Typography>

        {eeDetails?.version && (
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}
            >
              <strong>Version:</strong> {eeDetails.version}
            </Typography>
          </Box>
        )}

        {eeDetails?.assembly_number && (
          <Box sx={{ marginBottom: "8px" }}>
            <Typography
              variant="h6"
              sx={{ fontSize: "14px", fontWeight: "bold", marginBottom: "8px" }}
            >
              <strong>Assembly Number:</strong> {eeDetails.assembly_number}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

// Custom Project Node with multiple handles for different zones
const ProjectNode = ({ data }) => {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "6px",
        boxSizing: "border-box",
        overflow: "hidden",
        background: "black",
        color: "white",
        borderRadius: "50%",
        position: "relative",
      }}
    >
      {/* Multiple source handles for different zones */}
      <Handle
        type="source"
        position={Position.Top}
        id="source-top"
        style={{ top: "0%", left: "50%", transform: "translateX(-50%)" }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="source-top-left"
        style={{ top: "12%", left: "24%", transform: "translate(-50%, -50%)" }}
      />
      <Handle
        type="source"
        position={Position.Top}
        id="source-top-right"
        style={{ top: "12%", left: "76%", transform: "translate(-50%, -50%)" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source-bottom"
        style={{ bottom: "0%", left: "50%", transform: "translateX(-50%)" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source-bottom-left"
        style={{
          bottom: "12%",
          left: "24%",
          transform: "translate(-50%, 50%)",
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="source-bottom-right"
        style={{
          bottom: "12%",
          left: "76%",
          transform: "translate(-50%, 50%)",
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="source-left"
        style={{ left: "0%", top: "50%", transform: "translateY(-50%)" }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="source-right"
        style={{ right: "0%", top: "50%", transform: "translateY(-50%)" }}
      />

      <div style={CLAMPED_NODE_LABEL_STYLE}>{data.name}</div>
    </div>
  );
};

const buildZoneLabelLines = (label, maxLines, maxChars) => {
  const words = (label || "").toString().trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [""];

  const lines = [];
  let line = "";
  let overflow = false;

  const clampWord = (word) => {
    if (word.length <= maxChars) return { text: word, truncated: false };
    const cut = Math.max(1, maxChars - 3);
    return { text: `${word.slice(0, cut)}...`, truncated: true };
  };

  for (let i = 0; i < words.length; i += 1) {
    const { text: wordText, truncated } = clampWord(words[i]);
    overflow = overflow || truncated;

    if (!line) {
      line = wordText;
      continue;
    }

    if (`${line} ${wordText}`.length <= maxChars) {
      line = `${line} ${wordText}`;
      continue;
    }

    lines.push(line);
    if (lines.length === maxLines) {
      overflow = true;
      line = "";
      break;
    }
    line = wordText;
  }

  if (line) lines.push(line);

  if (lines.length > maxLines) {
    lines.length = maxLines;
    overflow = true;
  }

  if (overflow) {
    const lastIndex = lines.length - 1;
    const lastLine = lines[lastIndex] || "";
    if (!lastLine.endsWith("...")) {
      const cut = Math.max(1, maxChars - 3);
      lines[lastIndex] = `${lastLine.slice(0, cut)}...`;
    }
  }

  return lines;
};

// Custom node for zones: triangular green badge with centered white label
const ZoneNode = ({ data }) => {
  const label = (data?.labelText || "").toString();
  const lines = buildZoneLabelLines(label, 2, 10);
  const rotate = !!data?.rotate;
  const textY = rotate ? "45%" : "70%";
  const isFromFrontSide = !!data?.isFromFrontSide;
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "visible",
      }}
    >
      {isFromFrontSide &&
        (data?.labelText || "").toString().trim().toUpperCase() !== "FRONT" && (
          <Handle
            type="target"
            position={Position.Top}
          // make handles slightly visible for debugging; change back after verification
          // style={{ background: "rgba(48, 24, 185, 1)", width: 12, height: 12 }}
          />
        )}

      {/* Add top handle for REAR center zone to connect to the flat side */}
      {(data?.labelText || "").toString().trim().toUpperCase() === "REAR" && (
        <Handle
          type="target"
          position={Position.Top}
          id="zone-top-target"
          style={{
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      )}

      <svg
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid meet"
        style={{ width: "100%", height: "100%", overflow: "visible" }}
      >
        <polygon
          points="50,0 100,100 0,100"
          fill="#2E8B46"
          stroke="#0e5e22"
          strokeWidth="2"
          transform={rotate ? "rotate(180 50 50)" : undefined}
        />
        <text
          x="50%"
          y={textY}
          fill="white"
          fontWeight="700"
          fontSize="11"
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {lines.map((lineText, i) => (
            <tspan key={`${lineText}-${i}`} x="50%" dy={i === 0 ? "-6" : "13"}>
              {lineText}
            </tspan>
          ))}
        </text>
      </svg>
      {/* allow incoming connections to the bottom of the zone for special cases (e.g., REAR zones and FRONT zone) */}
      {(!isFromFrontSide ||
        (data?.labelText || "").toString().trim().toUpperCase() ===
        "FRONT") && (
          <Handle
            type="target"
            position={Position.Bottom}
            id="zone-bottom-target"
            style={{
              // visible debug color so we can confirm attachment in the UI
              // background: "rgba(255,0,0,0.35)",
              // width: 12,
              // height: 12,
              left: data?.isRear ? "50%" : "50%",
              transform: "translateX(-50%)",
            }}
          />
        )}
      {/* Source handle for connections to EE components */}
      {(data?.labelText || "").toString().trim().toUpperCase() === "FRONT" ? (
        <Handle
          type="source"
          position={Position.Top}
          id="zone-top-source"
          style={{
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      ) : (data?.labelText || "").toString().trim().toUpperCase() === "REAR" ? (
        // For REAR zone (rotated triangle), the pointed tip is visually at the bottom
        // So we need to place the handle at the bottom position for the visual tip
        <Handle
          type="source"
          position={Position.Bottom}
          id="zone-top-source"
          style={{
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      ) : (
        <Handle
          type="source"
          position={isFromFrontSide ? Position.Bottom : Position.Top}
          id="zone-bottom-source"
          style={{
            // visible debug color so we can confirm attachment in the UI
            // background: "rgba(255,0,0,0.35)",
            // width: 12,
            // height: 12,
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      )}
    </div>
  );
};

// Custom SWM Node with conditional handles
const SWMNode = ({ data }) => {
  const isRearZone = data?.isRearZone || false;
  const isFrontZone = data?.isFrontZone || false;
  const isCentralLeftZone = data?.isCentralLeftZone || false;
  const isRearLeftZone = data?.isRearLeftZone || false;
  const isRearRightZone = data?.isRearRightZone || false;

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "4px",
        boxSizing: "border-box",
        background: data.background || "#2897DC",
        color: "white",
        cursor: "pointer",
        transition: "all 0.2s ease",
        border: "1px solid rgba(255,255,255,0.3)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Conditional handles based on zone type */}
      {isRearZone ? (
        <Handle
          type="target"
          position={Position.Left}
          style={{
            left: "0%",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
      ) : isFrontZone ? (
        <Handle
          type="target"
          position={Position.Right}
          style={{
            right: "0%",
            top: "50%",
            transform: "translateY(-50%)",
          }}
        />
      ) : isCentralLeftZone || isRearLeftZone || isRearRightZone ? (
        <Handle
          type="target"
          position={Position.Top}
          style={{
            top: "0%",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      ) : (
        <Handle
          type="target"
          position={Position.Bottom}
          style={{
            bottom: "0%",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        />
      )}

      <h3
        style={{
          margin: 0,
          fontSize: "10px",
          fontWeight: "bold",
          textAlign: "center",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          lineHeight: "12px",
          maxHeight: "24px",
        }}
      >
        {data.name}
      </h3>
    </div>
  );
};

// Define constants for node dimensions and margins
const nodeWidth = 140;
const nodeHeight = 140;

const ZONE_DIRS = {
  TOP: { x: 0, y: -1 },
  FRONT_LEFT: { x: -0.7, y: -0.9 },
  FRONT_RIGHT: { x: 0.7, y: -0.9 },
  CENTER_LEFT: { x: -1.4, y: 0 },
  CENTER_RIGHT: { x: 1.4, y: 0 },
  BOTTOM_LEFT: { x: -0.7, y: 0.9 },
  BOTTOM_RIGHT: { x: 0.7, y: 0.9 },
  REAR: { x: 0, y: 1.1 },
  TOP_LEFT: { x: -1, y: -1 },
  TOP_RIGHT: { x: 1, y: -1 },
  BOTTOM: { x: 0, y: 1 },
};

const PER_ZONE_ANCHORS = {
  "FRONT LEFT": [
    ZONE_DIRS.TOP,
    ZONE_DIRS.FRONT_LEFT,
    ZONE_DIRS.CENTER_LEFT,
    ZONE_DIRS.BOTTOM_LEFT,
  ],
  "FRONT RIGHT": [
    ZONE_DIRS.TOP,
    ZONE_DIRS.FRONT_RIGHT,
    ZONE_DIRS.CENTER_RIGHT,
    ZONE_DIRS.BOTTOM_RIGHT,
  ],
  "REAR LEFT": [ZONE_DIRS.CENTER_LEFT, ZONE_DIRS.BOTTOM_LEFT, ZONE_DIRS.REAR],
  "REAR RIGHT": [
    ZONE_DIRS.CENTER_RIGHT,
    ZONE_DIRS.BOTTOM_RIGHT,
    ZONE_DIRS.REAR,
  ],
  "CENTRAL LEFT": [
    ZONE_DIRS.TOP_LEFT,
    ZONE_DIRS.CENTER_LEFT,
    ZONE_DIRS.BOTTOM_LEFT,
  ],
  "CENTRAL RIGHT": [
    ZONE_DIRS.TOP_RIGHT,
    ZONE_DIRS.CENTER_RIGHT,
    ZONE_DIRS.BOTTOM_RIGHT,
  ],
  FRONT: [
    ZONE_DIRS.TOP,
    ZONE_DIRS.FRONT_LEFT,
    ZONE_DIRS.FRONT_RIGHT,
    ZONE_DIRS.CENTER_LEFT,
    ZONE_DIRS.CENTER_RIGHT,
  ],
  REAR: [
    ZONE_DIRS.REAR,
    ZONE_DIRS.BOTTOM_LEFT,
    ZONE_DIRS.BOTTOM_RIGHT,
    ZONE_DIRS.CENTER_LEFT,
    ZONE_DIRS.CENTER_RIGHT,
  ],
};

const DEFAULT_ANCHOR_DIRS = [
  ZONE_DIRS.TOP_LEFT,
  ZONE_DIRS.TOP_RIGHT,
  ZONE_DIRS.BOTTOM_LEFT,
  ZONE_DIRS.BOTTOM_RIGHT,
  ZONE_DIRS.CENTER_LEFT,
  ZONE_DIRS.CENTER_RIGHT,
];

// Utility function to get node color based on selection and linking
const getNodeColor = (nodeId, selectedNodeId, linkedSwmIds) => {
  customConsole("nodeId, selectedNodeId, linkedSwmIds");
  customConsole({ nodeId, selectedNodeId, linkedSwmIds });
  if (nodeId === selectedNodeId) return "#00FF1A";
  if (
    selectedNodeId &&
    linkedSwmIds?.includes(Number(selectedNodeId?.replace("swm-", "")))
  )
    return "gold";
  return "#2897DC";
};

const getZoneNameKey = (zone) =>
  (zone?.name || "").toString().trim().toUpperCase();

const findZoneByName = (zones, name) =>
  (zones || []).find((z) => getZoneNameKey(z) === name);

const getMaxExpandedSwms = (zone, expandedNodes) => {
  if (!zone || !expandedNodes?.has(zone.id)) return 0;
  const ees = zone.children || [];
  return Math.max(
    ...ees.map((ee) =>
      expandedNodes.has(ee.id) ? (ee.children || []).length : 0,
    ),
    0,
  );
};

const getSwmExtension = (maxSwms, extraSpacing, width = nodeWidth) => {
  if (maxSwms <= 0) return 0;
  const baseEeSize = Math.min(width, 140);
  const swmSize = Math.max(36, Math.floor(baseEeSize * 0.55));
  const swmSpacing = swmSize + 12;
  return maxSwms * swmSpacing + extraSpacing + baseEeSize;
};

const calculateZoneOverlapOffsets = ({ zones, expandedNodes, nameToPos }) => {
  const minSafeDistance = 180;

  const centralLeftZone = findZoneByName(zones, "CENTRAL LEFT");
  const rearLeftZone = findZoneByName(zones, "REAR LEFT");
  const maxSwmsInCentralLeft = getMaxExpandedSwms(
    centralLeftZone,
    expandedNodes,
  );
  const rearLeftYOffset =
    centralLeftZone && rearLeftZone && maxSwmsInCentralLeft > 0
      ? Math.max(
        0,
        nameToPos["CENTRAL LEFT"].y +
        getSwmExtension(maxSwmsInCentralLeft, 30) +
        minSafeDistance -
        nameToPos["REAR LEFT"].y,
      )
      : 0;

  const centralRightZone = findZoneByName(zones, "CENTRAL RIGHT");
  const frontRightZone = findZoneByName(zones, "FRONT RIGHT");
  const maxSwmsInCentralRight = getMaxExpandedSwms(
    centralRightZone,
    expandedNodes,
  );
  const frontRightYOffset =
    centralRightZone && frontRightZone && maxSwmsInCentralRight > 0
      ? Math.max(
        0,
        nameToPos["FRONT RIGHT"].y -
        (nameToPos["CENTRAL RIGHT"].y -
          getSwmExtension(maxSwmsInCentralRight, 30) -
          minSafeDistance),
      )
      : 0;

  const rearZone = findZoneByName(zones, "REAR");
  const rearRightZone = findZoneByName(zones, "REAR RIGHT");
  const maxSwmsInRear = getMaxExpandedSwms(rearZone, expandedNodes);
  const rearRightXOffset =
    rearZone && rearRightZone && maxSwmsInRear > 0
      ? Math.max(
        0,
        nameToPos["REAR"].x +
        getSwmExtension(maxSwmsInRear, 30) +
        minSafeDistance -
        nameToPos["REAR RIGHT"].x,
      )
      : 0;

  const frontZone = findZoneByName(zones, "FRONT");
  const frontLeftZone = findZoneByName(zones, "FRONT LEFT");
  const maxSwmsInFront = getMaxExpandedSwms(frontZone, expandedNodes);
  const frontLeftXOffset =
    frontZone && frontLeftZone && maxSwmsInFront > 0
      ? Math.max(
        0,
        nameToPos["FRONT LEFT"].x -
        (nameToPos["FRONT"].x -
          getSwmExtension(maxSwmsInFront, 30) -
          minSafeDistance),
      )
      : 0;

  return {
    rearLeftYOffset,
    frontRightYOffset,
    rearRightXOffset,
    frontLeftXOffset,
  };
};

const isFrontZone = (zoneKeyUpper) =>
  zoneKeyUpper === "FRONT LEFT" ||
  zoneKeyUpper === "FRONT RIGHT" ||
  zoneKeyUpper === "FRONT";

const isBottomFlowZone = (zoneKeyUpper) =>
  zoneKeyUpper === "CENTRAL LEFT" ||
  zoneKeyUpper === "REAR LEFT" ||
  zoneKeyUpper === "REAR RIGHT";

const getEeSourceConfig = (zoneKeyUpper) => {
  if (zoneKeyUpper === "REAR LEFT" || zoneKeyUpper === "REAR RIGHT") {
    return {
      sourceHandle: "zone-bottom-source",
      sourcePosition: Position.Bottom,
    };
  }

  if (zoneKeyUpper === "FRONT" || zoneKeyUpper === "REAR") {
    return {
      sourceHandle: "zone-top-source",
      sourcePosition: Position.Top,
    };
  }

  return {
    sourceHandle: undefined,
    sourcePosition: Position.Top,
  };
};

const shouldRenderSwm = ({
  swm,
  selectedNodeId,
  swmLinkedMap,
  swmParentEeMap,
}) => {
  if (!selectedNodeId || !selectedNodeId.startsWith("swm-")) return true;

  const selSwmNum = Number(selectedNodeId.replace("swm-", ""));
  const currentSwmNum = Number(String(swm.id || "").replace("swm-", ""));

  const linkedToSelected = (swmLinkedMap.get(selSwmNum) || []).includes(
    currentSwmNum,
  );

  const isSame = selSwmNum === currentSwmNum;

  const sameParent =
    swmParentEeMap.get(selSwmNum) === swmParentEeMap.get(currentSwmNum);

  return isSame || linkedToSelected || sameParent;
};

const getHorizontalNudge = (zoneKeyUpper) => {
  const map = {
    "REAR LEFT": 28,
    "REAR RIGHT": 28,
    "FRONT RIGHT": 31,
    "FRONT LEFT": 30,
    "CENTRAL RIGHT": 33,
    "CENTRAL LEFT": 32,
  };
  return map[zoneKeyUpper] ?? 27;
};

const getSwmPosition = ({
  isRightPlacement,
  isLeftPlacement,
  swmBaseX,
  swmSpacing,
  sidx,
  y,
  zoneKeyUpper,
  x,
  isTopPlacement,
  swmBaseY,
}) => {
  if (isRightPlacement) {
    return { sx: swmBaseX + sidx * swmSpacing, sy: y + 30 };
  }

  if (isLeftPlacement) {
    return { sx: swmBaseX - sidx * swmSpacing, sy: y + 30 };
  }

  const sx = x + getHorizontalNudge(zoneKeyUpper);
  const sy = isTopPlacement
    ? swmBaseY - sidx * swmSpacing
    : swmBaseY + sidx * swmSpacing;

  return { sx, sy };
};

const getSwmEdgePositions = (zoneKeyUpper) => ({
  sourcePosition: isBottomFlowZone(zoneKeyUpper)
    ? Position.Bottom
    : undefined,
  targetPosition: isBottomFlowZone(zoneKeyUpper) ? Position.Top : undefined,
});

const renderSwms = ({
  ee,
  nodes,
  edges,
  zoneKeyUpper,
  selectedNodeId,
  swmLinkedMap,
  swmParentEeMap,
  x,
  y,
  currentEeSize,
}) => {
  const swmSize = Math.max(36, Math.floor(currentEeSize * 0.55));
  const swmSpacing = swmSize + 12;

  const isTopPlacement =
    zoneKeyUpper === "FRONT LEFT" ||
    zoneKeyUpper === "FRONT RIGHT" ||
    zoneKeyUpper === "CENTRAL RIGHT";

  const isRightPlacement = zoneKeyUpper === "REAR";
  const isLeftPlacement = zoneKeyUpper === "FRONT";

  const extraDownForRear =
    zoneKeyUpper === "REAR LEFT" || zoneKeyUpper === "REAR RIGHT"
      ? swmSpacing * 1.8
      : zoneKeyUpper === "CENTRAL LEFT"
        ? 50
        : 35;

  const swmBaseY = isTopPlacement
    ? y - currentEeSize / 2 - swmSpacing - 15
    : y + currentEeSize / 2 + extraDownForRear + swmSize / 2 + 15;

  const swmBaseX = isRightPlacement
    ? x + currentEeSize / 2 + swmSpacing + 15
    : isLeftPlacement
      ? x - currentEeSize / 2 - swmSpacing - 15
      : x;

  ee.children.forEach((swm, sidx) => {
    if (
      !shouldRenderSwm({
        swm,
        selectedNodeId,
        swmLinkedMap,
        swmParentEeMap,
      })
    )
      return;

    const { sx, sy } = getSwmPosition({
      isRightPlacement,
      isLeftPlacement,
      swmBaseX,
      swmSpacing,
      sidx,
      y,
      zoneKeyUpper,
      x,
      isTopPlacement,
      swmBaseY,
    });

    nodes.push({
      id: swm.id,
      type: "swmNode",
      data: {
        name: swm.name,
        isRearZone: isRightPlacement,
        isFrontZone: isLeftPlacement,
        isCentralLeftZone: zoneKeyUpper === "CENTRAL LEFT",
        isRearLeftZone: zoneKeyUpper === "REAR LEFT",
        isRearRightZone: zoneKeyUpper === "REAR RIGHT",
        background: getNodeColor(swm.id, selectedNodeId, swm.linkedSwmIds),
      },
      position: { x: sx, y: sy },
      style: { width: swmSize, height: swmSize },
    });

    const { sourcePosition, targetPosition } =
      getSwmEdgePositions(zoneKeyUpper);

    edges.push({
      id: `${ee.id}-${swm.id}`,
      source: ee.id,
      target: swm.id,
      type: "straight",
      animated: false,
      style: { stroke: "#333", strokeWidth: 2 },
      sourcePosition,
      targetPosition,
    });
  });
};

const getGridEeSize = (baseEeSize, minEeSize, count, shrinkAfter) =>
  count > shrinkAfter
    ? Math.max(minEeSize, baseEeSize - (count - shrinkAfter) * 8)
    : baseEeSize;

const getHorizontalRowPositions = ({
  count,
  zonePos,
  baseEeSize,
  minEeSize,
  gap,
  placeLeft,
}) => {
  const positions = [];
  if (count <= 0) return positions;

  const gridEeSize = getGridEeSize(baseEeSize, minEeSize, count, 6);
  const hSpacing = gridEeSize + gap;
  const extraHorizontalMargin = 220;
  const halfZone = Math.max(nodeWidth, nodeHeight) / 2;
  const y = zonePos.y;

  if (placeLeft) {
    const gridRightEdge = zonePos.x - halfZone - gap - extraHorizontalMargin;
    const startX = gridRightEdge - (count - 1) * hSpacing - gridEeSize / 2;
    for (let c = 0; c < count; c++) {
      const x = startX + c * hSpacing + gridEeSize / 2;
      positions[c] = { x, y, size: gridEeSize };
    }
    return positions;
  }

  const gridLeftEdge = zonePos.x + halfZone + gap + extraHorizontalMargin;
  const startX = gridLeftEdge + gridEeSize / 2;
  for (let c = 0; c < count; c++) {
    const x = startX + c * hSpacing;
    positions[c] = { x, y, size: gridEeSize };
  }
  return positions;
};

const getVerticalColumnPositions = ({
  count,
  zonePos,
  baseEeSize,
  minEeSize,
  gap,
  placeAbove,
}) => {
  const positions = [];
  if (count <= 0) return positions;

  const gridEeSize = getGridEeSize(baseEeSize, minEeSize, count, 4);
  const vSpacing = gridEeSize + gap;
  const extraVerticalMargin = 180;
  const halfZone = Math.max(nodeWidth, nodeHeight) / 2;
  const x = zonePos.x;

  if (placeAbove) {
    const gridBottomEdge = zonePos.y - halfZone - gap - extraVerticalMargin;
    const startY = gridBottomEdge - (count - 1) * vSpacing - gridEeSize / 2;
    for (let r = 0; r < count; r++) {
      const y = startY + r * vSpacing + gridEeSize / 2;
      positions[r] = { x, y, size: gridEeSize };
    }
    return positions;
  }

  const gridTopEdge = zonePos.y + halfZone + gap + extraVerticalMargin;
  const startY = gridTopEdge + gridEeSize / 2;
  for (let r = 0; r < count; r++) {
    const y = startY + r * vSpacing;
    positions[r] = { x, y, size: gridEeSize };
  }
  return positions;
};

const getLayeredAnchorPositions = ({
  ees,
  zonePos,
  baseEeSize,
  minEeSize,
  gap,
  anchorDirs,
}) => {
  const positions = [];
  const slotsPerLayer = anchorDirs.length;

  ees.forEach((ee, idx) => {
    const layer = Math.floor(idx / slotsPerLayer);
    const slot = idx % slotsPerLayer;
    const currentEeSize = Math.max(minEeSize, baseEeSize - layer * 12);
    const baseOffset =
      Math.max(nodeWidth, nodeHeight) / 2 + gap + layer * (currentEeSize + gap + 10);
    const dir = anchorDirs[slot];
    const x = zonePos.x + dir.x * baseOffset;
    const y = zonePos.y + dir.y * baseOffset;
    positions[idx] = { x, y, size: currentEeSize };
  });

  return positions;
};

const getEePositionsForZone = ({ zoneKeyUpper, ees, zonePos }) => {
  const count = ees.length;
  const baseEeSize = Math.min(nodeWidth, 140);
  const minEeSize = 50;
  const gap = 20;

  if (
    zoneKeyUpper === "FRONT LEFT" ||
    zoneKeyUpper === "FRONT RIGHT" ||
    zoneKeyUpper === "REAR LEFT" ||
    zoneKeyUpper === "REAR RIGHT" ||
    zoneKeyUpper === "CENTRAL LEFT" ||
    zoneKeyUpper === "CENTRAL RIGHT"
  ) {
    const placeLeft =
      zoneKeyUpper === "FRONT LEFT" ||
      zoneKeyUpper === "REAR LEFT" ||
      zoneKeyUpper === "CENTRAL LEFT";
    return getHorizontalRowPositions({
      count,
      zonePos,
      baseEeSize,
      minEeSize,
      gap,
      placeLeft,
    });
  }

  if (zoneKeyUpper === "FRONT") {
    return getVerticalColumnPositions({
      count,
      zonePos,
      baseEeSize,
      minEeSize,
      gap,
      placeAbove: true,
    });
  }

  if (zoneKeyUpper === "REAR") {
    return getVerticalColumnPositions({
      count,
      zonePos,
      baseEeSize,
      minEeSize,
      gap,
      placeAbove: false,
    });
  }

  const anchorDirs = PER_ZONE_ANCHORS[zoneKeyUpper] || DEFAULT_ANCHOR_DIRS;
  return getLayeredAnchorPositions({
    ees,
    zonePos,
    baseEeSize,
    minEeSize,
    gap,
    anchorDirs,
  });
};

const buildZoneNodeId = (zone) => {
  const seed =
    zone?.zone_detail_id ?? zone?.sequence ?? zone?.position ?? zone?.zone_name;
  const safeSeed = (seed ?? zone?._fallback_index ?? "unknown")
    .toString()
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase();
  return `zone-${safeSeed}`;
};

const getZonesSource = (projectDetails) => {
  const zonesFromZoneDetails = Array.isArray(projectDetails?.zone_details)
    ? projectDetails.zone_details
    : [];

  return zonesFromZoneDetails.length > 0
    ? zonesFromZoneDetails.map((zoneDetail, index) => ({
      zone_name: zoneDetail?.zone?.zone_name || zoneDetail?.zone_name || "",
      position: zoneDetail?.zone?.position || zoneDetail?.position || "",
      sequence: zoneDetail?.zone?.sequence ?? zoneDetail?.sequence,
      zone_detail_id: zoneDetail?.id ?? zoneDetail?.zone?.id,
      _fallback_index: index + 1,
    }))
    : (Array.isArray(projectDetails?.zones) ? projectDetails.zones : []).map(
      (zone, index) => ({
        zone_name: zone?.zone_name || "",
        position: zone?.position || "",
        sequence: zone?.sequence,
        zone_detail_id: zone?.id,
        _fallback_index: index + 1,
      }),
    );
};

const findZoneNodeIdForEe = (ee, projectDetails) => {
  const zonesSource = getZonesSource(projectDetails);
  if (zonesSource.length === 0) return null;

  const eeZoneDetails = ee?.zone_details || {};
  const eeSequence = eeZoneDetails?.sequence ?? eeZoneDetails?.zone?.sequence;
  const eeZoneName = (
    eeZoneDetails?.zone_name ||
    eeZoneDetails?.zone?.zone_name ||
    ""
  )
    .toString()
    .trim()
    .toUpperCase();
  const eeZonePosition = (
    eeZoneDetails?.position ||
    eeZoneDetails?.zone?.position ||
    ""
  )
    .toString()
    .trim()
    .toUpperCase();

  const matchedZone = zonesSource.find((zone) => {
    const zoneSequence = zone?.sequence;
    const zoneName = (zone?.zone_name || "").toString().trim().toUpperCase();
    const zonePosition = (zone?.position || "").toString().trim().toUpperCase();

    const bySequence =
      zoneSequence != null &&
      eeSequence != null &&
      Number(zoneSequence) === Number(eeSequence);
    const byName = !!zoneName && !!eeZoneName && zoneName === eeZoneName;
    const byPosition =
      !!zonePosition && !!eeZonePosition && zonePosition === eeZonePosition;

    return bySequence || byName || byPosition;
  });

  return matchedZone ? buildZoneNodeId(matchedZone) : null;
};

const toUpperTrim = (value) =>
  (value ?? "").toString().trim().toUpperCase();

const getZoneMatchKeys = (zone) => ({
  sequence: zone?.sequence,
  name: toUpperTrim(zone?.zone_name),
  position: toUpperTrim(zone?.position),
});

const getEeZoneMatchKeys = (ee) => {
  const eeZoneDetails = ee?.zone_details || {};
  return {
    sequence: eeZoneDetails?.sequence ?? eeZoneDetails?.zone?.sequence,
    name: toUpperTrim(eeZoneDetails?.zone_name || eeZoneDetails?.zone?.zone_name),
    position: toUpperTrim(eeZoneDetails?.position || eeZoneDetails?.zone?.position),
  };
};

const isSameZone = (zoneKeys, eeKeys) => {
  const bySequence =
    zoneKeys.sequence != null &&
    eeKeys.sequence != null &&
    Number(zoneKeys.sequence) === Number(eeKeys.sequence);
  const byName = !!zoneKeys.name && !!eeKeys.name && zoneKeys.name === eeKeys.name;
  const byPosition =
    !!zoneKeys.position &&
    !!eeKeys.position &&
    zoneKeys.position === eeKeys.position;
  return bySequence || byName || byPosition;
};

const getSwmChildren = (projectDetails, ee) => {
  const swmDetails = Array.isArray(projectDetails?.swm_details)
    ? projectDetails.swm_details
    : [];
  return swmDetails
    .filter((swm) => swm?.ee_id === ee.ee_id && swm?.version !== "")
    .map((swm) => ({
      id: `swm-${swm.swm_id}`,
      name: swm.name,
      version: swm.version,
      linkedSwmIds: swm.linked_swm_ids?.map((obj) => obj.id) || [],
    }));
};

const getEeChildrenForZone = (projectDetails, zone) => {
  const zoneKeys = getZoneMatchKeys(zone);
  const eeDetails = Array.isArray(projectDetails?.ee_details)
    ? projectDetails.ee_details
    : [];

  return eeDetails
    .filter((ee) => {
      if (ee?.version === "") return false;
      const eeKeys = getEeZoneMatchKeys(ee);
      return isSameZone(zoneKeys, eeKeys);
    })
    .map((ee) => ({
      id: `ee-${ee.ee_id}`,
      name: ee.description,
      version: ee.version,
      children: getSwmChildren(projectDetails, ee),
    }));
};

// Create hierarchy data
const createHierarchyData = (projectDetails) => {
  const zonesSource = getZonesSource(projectDetails);

  return {
    id: `project-${projectDetails.project_id}`,
    name: projectDetails.name,
    children: zonesSource.map((zone) => ({
      id: buildZoneNodeId(zone),
      name: zone.zone_name,
      children: getEeChildrenForZone(projectDetails, zone),
    })),
  };
};

// (removed duplicate top-level createNodesAndEdges to use component-scoped implementation)

// ZonalMindMapDialog Component - for opening mind map in dialog
const ZonalMindMapDialog = ({
  open,
  onClose,
  selectedProjectId,
  selectedVariantId,
  projectName = "Project Mind Map",
}) => {
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "95vw",
          height: "90vh",
          maxWidth: "none",
          maxHeight: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" component="div">
          {projectName} - Zonal Mind Map
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, overflow: "hidden" }}>
        <VlcZonalMindMap
          selectedProjectId={selectedProjectId}
          selectedVariantId={selectedVariantId}
          isDialog={true}
        />
      </DialogContent>
    </Dialog>
  );
};

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 23-07-2024
 * @description : Show the mind map.
 * @param : selectedProjectId
 * @return : The rendered mind map.
 */
function VlcZonalMindMap({
  selectedProjectId,
  selectedVariantId,
  isDialog = false,
}) {
  const dispatch = useDispatch();
  const lastRequestKeyRef = useRef("");
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [openDialog, setOpenDialog] = useState(false);

  /* useSelectors */
  const { projectDetailsMindMap } = useSelector(
    (state) => state.labCarReducer,
    shallowEqual,
  );
  const selectedVariantDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails,
  );

  /* Project */
  const [mindMapProjectDetails, setMindMapProjectDetails] = useState({});

  /* Nodes */
  const [flowNodes, setFlowNodes] = useState([]);
  const [flowEdges, setFlowEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [lastClickedZoneId, setLastClickedZoneId] = useState(null);
  const rfInstanceRef = useRef(null);

  // Hover dialog state
  const [hoverDialog, setHoverDialog] = useState({
    open: false,
    swmDetails: null,
    position: { x: 0, y: 0 },
    isLinkedSWM: false,
  });

  // EE Hover dialog state
  const [eeHoverDialog, setEeHoverDialog] = useState({
    open: false,
    eeDetails: null,
    position: { x: 0, y: 0 },
  });

  /* useEffects */
  // Fetch project details when project ID changes and reset expanded state
  useEffect(() => {
    if (selectedProjectId && selectedVariantId) {
      if (
        selectedVariantDetails?.project_id &&
        Number(selectedVariantDetails.project_id) !== Number(selectedProjectId)
      ) {
        setFlowNodes([]);
        setFlowEdges([]);
        setMindMapProjectDetails({});
        setExpandedNodes(new Set());
        setSelectedNode(null);
        return;
      }
      const requestKey = `${selectedProjectId}-${selectedVariantId}`;
      if (lastRequestKeyRef.current === requestKey) return;
      lastRequestKeyRef.current = requestKey;

      // Reset expanded nodes to show only project and zones when changing projects
      setExpandedNodes(new Set());
      setSelectedNode(null);
      setFlowNodes([]);
      setFlowEdges([]);
      setMindMapProjectDetails({});

      dispatch(
        HandleApiActions({
          ...labCar.get_project_details_mind_map,
          params: {
            project_id: selectedProjectId,
            variant_id: selectedVariantId,
          },
          show_toast: false,
        }),
      );
    }
  }, [
    selectedProjectId,
    selectedVariantId,
    selectedVariantDetails?.project_id,
    dispatch,
  ]);

  // Update mind map details and ensure clean state
  useEffect(() => {
    if (projectDetailsMindMap && projectDetailsMindMap.zones) {
      if (
        selectedProjectId &&
        Number(projectDetailsMindMap?.project_id) !== Number(selectedProjectId)
      ) {
        return;
      }
      setMindMapProjectDetails(projectDetailsMindMap);
      // Ensure we start with a clean slate - only project and zones visible
      setExpandedNodes(new Set());
      setSelectedNode(null);
    }
  }, [projectDetailsMindMap, selectedProjectId]);

  const createNodesAndEdges = useCallback(
    (hierarchyData, selectedNodeId, expandedNodes) => {
      const nodes = [];
      const edges = [];

      // Fixed positions for up to 8 zones around center
      const ZONE_POSITIONS = [
        { x: -320, y: -220 }, // top-left
        { x: 0, y: -360 }, // top
        { x: 320, y: -220 }, // top-right
        { x: 420, y: 0 }, // right
        { x: 320, y: 220 }, // bottom-right
        { x: 0, y: 360 }, // bottom
        { x: -320, y: 220 }, // bottom-left
        { x: -420, y: 0 }, // left
      ];

      // Project at center - needs multiple source handles for different zones
      const projectId = hierarchyData.id;
      nodes.push({
        id: projectId,
        type: "projectNode",
        data: {
          name: hierarchyData.name,
        },
        position: { x: 0, y: 0 },
        style: {
          width: nodeWidth,
          height: nodeHeight,
        },
      });

      // Place zones at fixed positions. Prefer positioning by zone name (FRONT LEFT, FRONT RIGHT, REAR LEFT, REAR RIGHT,
      // CENTRAL LEFT, CENTRAL RIGHT, FRONT, REAR). If name is not recognized, fall back to the default ZONE_POSITIONS.
      const zones = hierarchyData.children || [];
      const zonePositionsMap = new Map();

      const NAME_TO_POS = {
        "FRONT LEFT": { x: -320, y: -220 },
        "FRONT RIGHT": { x: 320, y: -220 },
        "REAR LEFT": { x: -320, y: 220 },
        "REAR RIGHT": { x: 320, y: 220 },
        "CENTRAL LEFT": { x: -420, y: 0 },
        "CENTRAL RIGHT": { x: 420, y: 0 },
        FRONT: { x: 0, y: -360 },
        REAR: { x: 0, y: 360 },
      };

      const {
        rearLeftYOffset,
        frontRightYOffset,
        rearRightXOffset,
        frontLeftXOffset,
      } = calculateZoneOverlapOffsets({
        zones,
        expandedNodes,
        nameToPos: NAME_TO_POS,
      });

      // zones.forEach((zone, i) => {
      //   const nameKey = (zone.name || "").toString().trim().toUpperCase();
      //   // whether this zone is a front-side zone (used by ZoneNode handles)
      //   const isFromFrontSide =
      //     nameKey === "FRONT LEFT" ||
      //     nameKey === "FRONT RIGHT" ||
      //     nameKey === "FRONT";
      //   const isRear = nameKey === "REAR LEFT" || nameKey === "REAR RIGHT";
      //   let pos =
      //     NAME_TO_POS[nameKey] || ZONE_POSITIONS[i % ZONE_POSITIONS.length];

      //   // Apply offset to REAR LEFT zone if it would overlap with CENTRAL LEFT SWMs
      //   if (nameKey === "REAR LEFT" && rearLeftYOffset > 0) {
      //     pos = { ...pos, y: pos.y + rearLeftYOffset };
      //   }

      //   // Apply offset to FRONT RIGHT zone if it would overlap with CENTRAL RIGHT SWMs
      //   if (nameKey === "FRONT RIGHT" && frontRightYOffset > 0) {
      //     pos = { ...pos, y: pos.y - frontRightYOffset };
      //   }

      //   // Apply offset to REAR RIGHT zone if it would overlap with REAR zone SWMs
      //   if (nameKey === "REAR RIGHT" && rearRightXOffset > 0) {
      //     pos = { ...pos, x: pos.x + rearRightXOffset };
      //   }

      //   // Apply offset to FRONT LEFT zone if it would overlap with FRONT zone SWMs
      //   if (nameKey === "FRONT LEFT" && frontLeftXOffset > 0) {
      //     pos = { ...pos, x: pos.x - frontLeftXOffset };
      //   }

      //   zonePositionsMap.set(zone.id, pos);
      //   // Preserve square shape and existing styling for zone nodes (do not change to triangle or modify colors)
      //   const shouldRotate =
      //     nameKey === "REAR LEFT" ||
      //     nameKey === "REAR RIGHT" ||
      //     nameKey === "REAR";
      //   nodes.push({
      //     id: zone.id,
      //     type: "zoneNode",
      //     data: {
      //       labelText: zone.name,
      //       rotate: shouldRotate,
      //       isFromFrontSide,
      //       isRear,
      //     },
      //     position: { x: pos.x, y: pos.y },
      //     style: { width: nodeWidth, height: nodeHeight },
      //     // zone emits connections from its bottom so lines go toward EE nodes
      //     sourcePosition: Position.Top,
      //     // zones still accept incoming from top if needed
      //     // default target at top; override for REAR zones below
      //     targetPosition: Position.Top,
      //   });

      //   // project -> zone edge: use specific handles based on zone direction
      //   const zoneNameUpper = (zone.name || "").toString().trim().toUpperCase();
      //   const isRearZone =
      //     zoneNameUpper === "REAR LEFT" || zoneNameUpper === "REAR RIGHT";
      //   const isFrontZone =
      //     zoneNameUpper === "FRONT LEFT" || zoneNameUpper === "FRONT RIGHT";
      //   const isFrontCenterZone = zoneNameUpper === "FRONT";
      //   const isRearCenterZone = zoneNameUpper === "REAR";

      //   // Determine which source handle to use on the project node
      //   let sourceHandle = "source-bottom"; // default
      //   if (zoneNameUpper === "FRONT LEFT") {
      //     sourceHandle = "source-top-left";
      //   } else if (zoneNameUpper === "FRONT RIGHT") {
      //     sourceHandle = "source-top-right";
      //   } else if (isFrontCenterZone) {
      //     sourceHandle = "source-top";
      //   } else if (zoneNameUpper === "REAR LEFT") {
      //     sourceHandle = "source-bottom-left";
      //   } else if (zoneNameUpper === "REAR RIGHT") {
      //     sourceHandle = "source-bottom-right";
      //   } else if (isRearCenterZone) {
      //     sourceHandle = "source-bottom";
      //   } else if (zoneNameUpper === "CENTRAL LEFT") {
      //     sourceHandle = "source-left";
      //   } else if (zoneNameUpper === "CENTRAL RIGHT") {
      //     sourceHandle = "source-right";
      //   }

      //   edges.push({
      //     id: `${projectId}-${zone.id}`,
      //     source: projectId,
      //     target: zone.id,
      //     type: "step", // orthogonal routing
      //     animated: false,
      //     style: { stroke: "#333", strokeWidth: 2 },
      //     sourceHandle: sourceHandle,
      //     targetHandle: isRearZone
      //       ? "zone-bottom-target"
      //       : isFrontCenterZone
      //         ? "zone-bottom-target"
      //         : isRearCenterZone
      //           ? "zone-top-target"
      //           : undefined,
      //     targetPosition: isRearZone
      //       ? Position.Bottom
      //       : isFrontCenterZone
      //         ? Position.Bottom
      //         : isRearCenterZone
      //           ? Position.Top
      //           : Position.Top,
      //   });
      // });

      // Build helper maps for SWM relationships so we can show only linked SWMs
      // in other EEs when a SWM is selected.

      const isFrontSideZone = (nameKey) =>
        nameKey === "FRONT LEFT" ||
        nameKey === "FRONT RIGHT" ||
        nameKey === "FRONT";

      const isRearZoneType = (nameKey) =>
        nameKey === "REAR LEFT" || nameKey === "REAR RIGHT";

      const shouldRotateZone = (nameKey) =>
        nameKey === "REAR LEFT" ||
        nameKey === "REAR RIGHT" ||
        nameKey === "REAR";

      const getZonePosition = (nameKey, i) => {
        let pos =
          NAME_TO_POS[nameKey] || ZONE_POSITIONS[i % ZONE_POSITIONS.length];

        if (nameKey === "REAR LEFT" && rearLeftYOffset > 0) {
          pos = { ...pos, y: pos.y + rearLeftYOffset };
        }

        if (nameKey === "FRONT RIGHT" && frontRightYOffset > 0) {
          pos = { ...pos, y: pos.y - frontRightYOffset };
        }

        if (nameKey === "REAR RIGHT" && rearRightXOffset > 0) {
          pos = { ...pos, x: pos.x + rearRightXOffset };
        }

        if (nameKey === "FRONT LEFT" && frontLeftXOffset > 0) {
          pos = { ...pos, x: pos.x - frontLeftXOffset };
        }

        return pos;
      };

      const getSourceHandle = (nameKey) => {
        const map = {
          "FRONT LEFT": "source-top-left",
          "FRONT RIGHT": "source-top-right",
          FRONT: "source-top",
          "REAR LEFT": "source-bottom-left",
          "REAR RIGHT": "source-bottom-right",
          REAR: "source-bottom",
          "CENTRAL LEFT": "source-left",
          "CENTRAL RIGHT": "source-right",
        };

        return map[nameKey] || "source-bottom";
      };

      const getTargetConfig = (nameKey) => {
        const isRearZone = nameKey === "REAR LEFT" || nameKey === "REAR RIGHT";
        const isFrontCenterZone = nameKey === "FRONT";
        const isRearCenterZone = nameKey === "REAR";

        return {
          targetHandle: isRearZone
            ? "zone-bottom-target"
            : isFrontCenterZone
              ? "zone-bottom-target"
              : isRearCenterZone
                ? "zone-top-target"
                : undefined,

          targetPosition: isRearZone
            ? Position.Bottom
            : isFrontCenterZone
              ? Position.Bottom
              : isRearCenterZone
                ? Position.Top
                : Position.Top,
        };
      };

      zones.forEach((zone, i) => {
        const nameKey = getZoneNameKey(zone);

        const isFromFrontSide = isFrontSideZone(nameKey);
        const isRear = isRearZoneType(nameKey);

        const pos = getZonePosition(nameKey, i);

        zonePositionsMap.set(zone.id, pos);

        nodes.push({
          id: zone.id,
          type: "zoneNode",
          data: {
            labelText: zone.name,
            rotate: shouldRotateZone(nameKey),
            isFromFrontSide,
            isRear,
          },
          position: { x: pos.x, y: pos.y },
          style: { width: nodeWidth, height: nodeHeight },
          sourcePosition: Position.Top,
          targetPosition: Position.Top,
        });

        const { targetHandle, targetPosition } = getTargetConfig(nameKey);

        edges.push({
          id: `${projectId}-${zone.id}`,
          source: projectId,
          target: zone.id,
          type: "step",
          animated: false,
          style: { stroke: "#333", strokeWidth: 2 },
          sourceHandle: getSourceHandle(nameKey),
          targetHandle,
          targetPosition,
        });
      });

      const swmLinkedMap = new Map(); // numeric swmId -> array of linked numeric ids
      const swmParentEeMap = new Map(); // numeric swmId -> parent ee node id (ee-<id>)
      // zones.forEach((z) => {
      //   (z.children || []).forEach((ee) => {
      //     (ee.children || []).forEach((swm) => {
      //       const swmNum = Number(String(swm.id || "").replace("swm-", ""));
      //       swmLinkedMap.set(swmNum, swm.linkedSwmIds || []);
      //       swmParentEeMap.set(swmNum, ee.id);
      //     });
      //   });
      // });

      const processSwm = (swm, ee, swmLinkedMap, swmParentEeMap) => {
        const swmNum = Number(String(swm.id || "").replace("swm-", ""));

        swmLinkedMap.set(swmNum, swm.linkedSwmIds || []);
        swmParentEeMap.set(swmNum, ee.id);
      };
      const processEe = (ee, swmLinkedMap, swmParentEeMap) => {
        (ee.children || []).forEach((swm) => {
          processSwm(swm, ee, swmLinkedMap, swmParentEeMap);
        });
      };
      const processZone = (z, swmLinkedMap, swmParentEeMap) => {
        (z.children || []).forEach((ee) => {
          processEe(ee, swmLinkedMap, swmParentEeMap);
        });
      };
      zones.forEach((z) => {
        processZone(z, swmLinkedMap, swmParentEeMap);
      });

      // Cluster placement does not use sector math; clusters will be anchored per-zone

      zones.forEach((zone, zi) => {
        if (!expandedNodes.has(zone.id)) return;
        const ees = zone.children || [];
        if (ees.length === 0) return;

        const zonePos = zonePositionsMap.get(zone.id) || { x: 0, y: 0 };
        const zoneKeyUpper = getZoneNameKey(zone);
        const positions = getEePositionsForZone({
          zoneKeyUpper,
          ees,
          zonePos,
        });



        ees.forEach((ee, idx) => {
          const posInfo = positions[idx];
          if (!posInfo) return;

          const { x, y, size: currentEeSize } = posInfo;

          // NODE
          nodes.push({
            id: ee.id,
            data: {
              label: (
                <div
                  style={{
                    width: "100%",
                    padding: "6px",
                    boxSizing: "border-box",
                  }}
                >
                  <div style={CLAMPED_NODE_LABEL_STYLE}>{ee.name}</div>
                </div>
              ),
            },
            position: { x, y },
            targetPosition: isFrontZone(zoneKeyUpper)
              ? Position.Bottom
              : Position.Top,
            sourcePosition:
              zoneKeyUpper === "REAR"
                ? Position.Right
                : zoneKeyUpper === "FRONT"
                  ? Position.Left
                  : isBottomFlowZone(zoneKeyUpper)
                    ? Position.Bottom
                    : Position.Top,
            style: {
              width: currentEeSize,
              height: currentEeSize,
              borderRadius: "50%",
              background: "black",
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
            },
          });

          // EDGE
          const { sourceHandle, sourcePosition } =
            getEeSourceConfig(zoneKeyUpper);

          const fromFront = isFrontZone(zoneKeyUpper);

          edges.push({
            id: `${zone.id}-${ee.id}`,
            source: zone.id,
            target: ee.id,
            type: "step",
            sourceHandle,
            sourcePosition,
            targetPosition: fromFront ? Position.Bottom : Position.Top,
            animated: false,
            style: { stroke: "#333", strokeWidth: 2 },
          });

          // DEBUG
          if (
            zoneKeyUpper === "REAR LEFT" ||
            zoneKeyUpper === "REAR RIGHT" ||
            zoneKeyUpper === "REAR"
          ) {
            console.log("[ZonalMindMap] created rear-zone edge:", {
              edgeId: `${zone.id}-${ee.id}`,
            });
          }

          // SWM RENDER
          if (
            expandedNodes.has(ee.id) &&
            ee.children &&
            ee.children.length > 0
          ) {
            renderSwms({
              ee,
              nodes,
              edges,
              zoneKeyUpper,
              selectedNodeId,
              swmLinkedMap,
              swmParentEeMap,
              x,
              y,
              currentEeSize,
            });
          }
        });

        // (removed legacy radial EE placement) grid-based cluster placement used above
      });

      return { nodes, edges };
    },
    [],
  );

  // After node/edge generation, wire up the component behavior (handlers, effects, render)
  // Generate nodes/edges whenever project details or expanded/selected state changes
  useEffect(() => {
    if (mindMapProjectDetails && mindMapProjectDetails.zones) {
      const hierarchyData = createHierarchyData(mindMapProjectDetails);
      const { nodes, edges } = createNodesAndEdges(
        hierarchyData,
        selectedNode,
        expandedNodes,
      );

      // Debug: print edges and rear-zone-specific edges so we can verify sourceHandle/sourcePosition
      // eslint-disable-next-line no-console
      console.log("[ZonalMindMap] generated edges:", edges);
      // eslint-disable-next-line no-console
      console.log(
        "[ZonalMindMap] rear-zone edges:",
        edges.filter((e) => /zone-.*-ee-/.test(e.id) || /zone-.*-/.test(e.id)),
      );

      setFlowNodes(nodes);
      setFlowEdges(edges);
    }
  }, [mindMapProjectDetails, selectedNode, expandedNodes, createNodesAndEdges]);

  // When a zone is opened, wait for nodes to render (expandedNodes changed) then fitView to show full structure
  useEffect(() => {
    if (!lastClickedZoneId) return;
    // give React Flow a tick to render the new nodes/edges, then fit view
    const t = setTimeout(() => {
      if (
        rfInstanceRef &&
        rfInstanceRef.current &&
        rfInstanceRef.current.fitView
      ) {
        rfInstanceRef.current.fitView({ padding: 0.1 });
      }
      setLastClickedZoneId(null);
    }, 140); // slight delay to let DOM update
    return () => clearTimeout(t);
  }, [lastClickedZoneId]);

  const onNodesChange = useCallback(
    (changes) => setFlowNodes((nds) => applyNodeChanges(changes, nds)),
    [],
  );

  const onEdgesChange = useCallback(
    (changes) => setFlowEdges((eds) => applyEdgeChanges(changes, eds)),
    [],
  );

  const onConnect = useCallback(
    (params) => setFlowEdges((eds) => addEdge(params, eds)),
    [],
  );



  // ================= HELPERS =================
  const getMousePosition = (event) => ({
    x: event.clientX || event.pageX,
    y: event.clientY || event.pageY,
  });

  const getSwmId = (nodeId) => Number(nodeId.replace("swm-", ""));
  const getEeId = (nodeId) => Number(nodeId.replace("ee-", ""));

  const findSwmDetails = (swmIdNum, swmDetailsList) =>
    swmDetailsList?.find((s) => Number(s.swm_id) === swmIdNum);

  const findEeDetails = (eeIdNum, eeDetailsList) =>
    eeDetailsList?.find((ee) => Number(ee.ee_id) === eeIdNum);

  // ================= LINKED SWM LOGIC =================
  const getLinkedSwmInfo = ({
    nodeId,
    selectedNode,
    mindMapProjectDetails,
    swmIdNum,
  }) => {
    let isLinkedSWM = false;
    let networkInterfaceInfo = null;

    if (
      selectedNode &&
      selectedNode.startsWith("swm-") &&
      selectedNode !== nodeId
    ) {
      const selectedSwmIdNum = Number(selectedNode.replace("swm-", ""));

      const selectedSwmDetails = mindMapProjectDetails.swm_details?.find(
        (s) => Number(s.swm_id) === selectedSwmIdNum,
      );

      if (selectedSwmDetails?.linked_swm_ids) {
        const linkedSWMObj = selectedSwmDetails.linked_swm_ids.find(
          (linkedObj) => Number(linkedObj.id) === swmIdNum,
        );

        if (linkedSWMObj) {
          isLinkedSWM = true;
          networkInterfaceInfo =
            linkedSWMObj.network_interface || "Network not defined";
        }
      }
    }

    return { isLinkedSWM, networkInterfaceInfo };
  };

  // ================= ENHANCED SWM =================
  const buildEnhancedSwmDetails = (
    swmDetails,
    isLinkedSWM,
    networkInterfaceInfo,
  ) => ({
    ...swmDetails,
    network_interface: isLinkedSWM
      ? {
        interface_name: networkInterfaceInfo,
        protocol: networkInterfaceInfo,
      }
      : swmDetails.network_interface,
  });

  // ================= SWM HANDLER =================
  const handleSwmHover = ({
    node,
    mousePos,
    mindMapProjectDetails,
    selectedNode,
    setHoverDialog,
  }) => {
    const swmIdNum = getSwmId(node.id);

    const swmDetails = findSwmDetails(
      swmIdNum,
      mindMapProjectDetails.swm_details,
    );

    if (!swmDetails) return;

    const { isLinkedSWM, networkInterfaceInfo } = getLinkedSwmInfo({
      nodeId: node.id,
      selectedNode,
      mindMapProjectDetails,
      swmIdNum,
    });

    const enhancedSwmDetails = buildEnhancedSwmDetails(
      swmDetails,
      isLinkedSWM,
      networkInterfaceInfo,
    );

    setHoverDialog({
      open: true,
      swmDetails: enhancedSwmDetails,
      position: {
        x: mousePos.x + 15,
        y: mousePos.y - 10,
      },
      isLinkedSWM,
    });
  };

  // ================= EE HANDLER =================
  const handleEeHover = ({
    node,
    mousePos,
    mindMapProjectDetails,
    setEeHoverDialog,
  }) => {
    const eeIdNum = getEeId(node.id);

    const eeDetails = findEeDetails(eeIdNum, mindMapProjectDetails.ee_details);

    if (!eeDetails) return;

    setEeHoverDialog({
      open: true,
      eeDetails,
      position: {
        x: mousePos.x + 15,
        y: mousePos.y - 10,
      },
    });
  };

  // ================= MAIN CALLBACK =================
  const handleNodeMouseEnter = useCallback(
    (event, node) => {
      const mousePos = getMousePosition(event);

      if (node.id.startsWith("swm-")) {
        handleSwmHover({
          node,
          mousePos,
          mindMapProjectDetails,
          selectedNode,
          setHoverDialog,
        });
        return;
      }

      if (node.id.startsWith("ee-")) {
        handleEeHover({
          node,
          mousePos,
          mindMapProjectDetails,
          setEeHoverDialog,
        });
      }
    },
    [
      mindMapProjectDetails.swm_details,
      mindMapProjectDetails.ee_details,
      selectedNode,
    ],
  );
  const handleNodeMouseLeave = useCallback((event, node) => {
    if (node.id.startsWith("swm-")) {
      setHoverDialog({
        open: false,
        swmDetails: null,
        position: { x: 0, y: 0 },
        isLinkedSWM: false,
      });
    } else if (node.id.startsWith("ee-")) {
      setEeHoverDialog({
        open: false,
        eeDetails: null,
        position: { x: 0, y: 0 },
      });
    }
  }, []);


  // ================= HELPERS =================
  const isZoneNode = (id) => id.startsWith("zone-");
  const isEeNode = (id) => id.startsWith("ee-");
  const isSwmNode = (id) => id.startsWith("swm-");

  const toggleExpansion = (nodeId, expandedSet) => {
    const newExpanded = new Set(expandedSet);
    const willOpen = !newExpanded.has(nodeId);

    if (willOpen) newExpanded.add(nodeId);
    else newExpanded.delete(nodeId);

    return { newExpanded, willOpen };
  };

  const expandEeAndZone = ({ eeId, newExpanded, mindMapProjectDetails }) => {
    const eeNodeId = `ee-${eeId}`;
    newExpanded.add(eeNodeId);

    const parentEeObj = mindMapProjectDetails.ee_details.find(
      (ee) => Number(ee.ee_id) === Number(eeId),
    );

    if (!parentEeObj) return;

    const zoneNodeId = findZoneNodeIdForEe(parentEeObj, mindMapProjectDetails);

    if (zoneNodeId) newExpanded.add(zoneNodeId);
  };

  const handleZoneOrEeClick = ({
    node,
    expandedNodes,
    setExpandedNodes,
    setLastClickedZoneId,
  }) => {
    const { newExpanded, willOpen } = toggleExpansion(node.id, expandedNodes);

    setExpandedNodes(newExpanded);

    if ((isZoneNode(node.id) || isEeNode(node.id)) && willOpen) {
      setLastClickedZoneId(node.id);
    }
  };

  const handleSwmClick = ({
    node,
    expandedNodes,
    mindMapProjectDetails,
    setExpandedNodes,
  }) => {
    if (!mindMapProjectDetails?.swm_details) return;

    const newExpanded = new Set(expandedNodes);

    const swmIdNum = Number(node.id.replace("swm-", ""));
    const swmObj = mindMapProjectDetails.swm_details.find(
      (s) => Number(s.swm_id) === swmIdNum,
    );

    if (!swmObj) return;

    // Expand parent EE + zone
    expandEeAndZone({
      eeId: swmObj.ee_id,
      newExpanded,
      mindMapProjectDetails,
    });

    // Expand linked SWMs
    const linkedIds = swmObj.linked_swm_ids?.map((obj) => obj.id) || [];

    linkedIds.forEach((lid) => {
      const linkedSwm = mindMapProjectDetails.swm_details.find(
        (s) => Number(s.swm_id) === Number(lid),
      );

      if (!linkedSwm) return;

      expandEeAndZone({
        eeId: linkedSwm.ee_id,
        newExpanded,
        mindMapProjectDetails,
      });
    });

    setExpandedNodes(newExpanded);
  };

  // ================= MAIN CALLBACK =================
  const handleNodeClick = useCallback(
    (event, node) => {
      setSelectedNode(node.id);

      if (isZoneNode(node.id) || isEeNode(node.id)) {
        handleZoneOrEeClick({
          node,
          expandedNodes,
          setExpandedNodes,
          setLastClickedZoneId,
        });
        return;
      }

      if (isSwmNode(node.id)) {
        handleSwmClick({
          node,
          expandedNodes,
          mindMapProjectDetails,
          setExpandedNodes,
        });
      }
    },
    [expandedNodes, mindMapProjectDetails],
  );
  const animatedEdges = useMemo(() => {
    return flowEdges.map((edge) => {
      if (edge.source === selectedNode || edge.target === selectedNode) {
        return { ...edge, animated: true };
      }
      return { ...edge, animated: false };
    });
  }, [flowEdges, selectedNode]);

  // If not in dialog mode, show clickable preview
  if (!isDialog) {
    return (
      <>
        <Paper
          elevation={2}
          sx={{
            p: 3,
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              elevation: 6,
              transform: "translateY(-2px)",
              bgcolor: "#f8f9fa",
            },
            border: "1px solid #e0e0e0",
            borderRadius: 2,
            textAlign: "center",
            mt: 2,
          }}
          onClick={() => setOpenDialog(true)}
        >
          <Box sx={{ py: 2 }}>
            <Typography
              variant="h5"
              gutterBottom
              color="primary"
              sx={{ fontWeight: "bold" }}
            >
              🗺️ Zonal Mind Map
            </Typography>
            <Typography variant="h6" color="text.primary" gutterBottom>
              {mindMapProjectDetails?.name || "Project"} Architecture
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mt: 2, mb: 3 }}
            >
              Explore the interactive visualization of your project's
              architecture, zones, EE components, and software modules
            </Typography>
            <Typography variant="body2" sx={{ fontStyle: "italic" }}>
              Click to open interactive mind map in full screen
            </Typography>
          </Box>
        </Paper>

        {/* Dialog for full mind map */}
        <ZonalMindMapDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          selectedProjectId={selectedProjectId}
          selectedVariantId={selectedVariantId}
          projectName={mindMapProjectDetails?.name}
        />
      </>
    );
  }

  // Full mind map for dialog mode
  return (
    <Box sx={isDialog ? dialogStyles.mainContainer : styles.mainContainer}>
      <ReactFlow
        key={`zonal-rf-${selectedProjectId || "na"}-${selectedVariantId || "na"}`}
        nodes={flowNodes}
        edges={animatedEdges}
        nodeTypes={{
          zoneNode: ZoneNode,
          projectNode: ProjectNode,
          swmNode: SWMNode,
        }}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={handleNodeClick}
        onNodeMouseEnter={handleNodeMouseEnter}
        onNodeMouseLeave={handleNodeMouseLeave}
        onInit={(instance) => (rfInstanceRef.current = instance)}
        fitView
        minZoom={0.1}
        maxZoom={4}
        defaultZoom={isDialog ? 0.3 : 0.5}
      >
        <Background />
        <Controls />
      </ReactFlow>

      {/* SWM Hover Tooltip */}
      <SWMHoverTooltip
        open={hoverDialog.open}
        swmDetails={hoverDialog.swmDetails}
        position={hoverDialog.position}
        isLinkedSWM={hoverDialog.isLinkedSWM}
      />

      {/* EE Hover Tooltip */}
      <EEHoverTooltip
        open={eeHoverDialog.open}
        eeDetails={eeHoverDialog.eeDetails}
        position={eeHoverDialog.position}
      />
    </Box>
  );
}

export default VlcZonalMindMap;
export { ZonalMindMapDialog };

// Styles
const styles = {
  mainContainer: {
    // height: "100vh",
    height: "520px",
    width: "100%",
    backgroundColor: "#F5F5F5",
    border: "1px solid grey",
    borderRadius: 1,
    padding: "20px",
    mt: 1,
    boxShadow: 4,
  },
};

const dialogStyles = {
  mainContainer: {
    height: "calc(90vh - 100px)", // Adjust for dialog header
    width: "100%",
    backgroundColor: "#F5F5F5",
    border: "none", // Remove border since it's in dialog
    borderRadius: 0, // Remove border radius
    padding: "10px", // Reduce padding
    mt: 0, // Remove margin top
    boxShadow: "none", // Remove box shadow
  },
};
